import { useCallback, useMemo, useContext } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import HelpPane from 'components/Common/HelpPane'
import Modal from 'LuxKit/Modal/Modal'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import ModalContext from 'contexts/ModalContext'

const USPItem = styled(Clickable)`
  display: block;

  & >:last-child {
    display: inline;
    vertical-align: middle;
  }
`
interface Props {
  offer: App.BundleOffer
  className?: string
}

type ModalTypes = 'bestPrice'

function BundleAndSaveUSPs(props: Props) {
  const { offer, className } = props
  const intl = useIntl()

  const showModal = useContext(ModalContext)
  const onItemClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const modalType = e.currentTarget.dataset.type as ModalTypes
    const modalTitle = intl.formatMessage({
      id: '5owRpw0X___wHjWkyQoj',
      defaultMessage: 'Our best price guarantee',
    })
    showModal(<Modal
      title={modalTitle}
    >
      {modalType === 'bestPrice' && <VerticalSpacer gap={16}>
        <BodyText variant="large">
          {intl.formatMessage({
            id: 'Di5OA_mmliEx5XWve1AN',
            defaultMessage: 'We negotiate the best prices so you don’t have to. In the unlikely event that you find a better price for the same offer, we promise to match it.',
          })}
        </BodyText>
        <BodyText variant="large">
          <Heading variant="heading5">
            {intl.formatMessage({
              id: 'S1lNPp5vqwVFujreJRxI',
              defaultMessage: '1. Compare the same offer',
            })}
          </Heading>
          {intl.formatMessage({
            id: 'Nx7k9qxQHp3wYXsGiPAl',
            defaultMessage: 'Make sure the offer you’ve found is for the same hotel with the same VIP inclusions as our package.',
          })}
        </BodyText>
        <BodyText variant="large">
          <Heading variant="heading5">
            {intl.formatMessage({
              id: 'srqEWO9VS4Fmw7DvK1sv',
              defaultMessage: '2. Record the better price',
            })}
          </Heading>
          {intl.formatMessage({
            id: 'iJxF8zrBBjwdk1iNBjL2',
            defaultMessage: 'Take a website screenshot or a photo of a catalogue so you can send it to us.',
          })}
        </BodyText>
        <BodyText variant="large">
          <Heading variant="heading5">
            {intl.formatMessage({
              id: 'l9H4UXkf3PP_y2Ev0hpq',
              defaultMessage: '3. We’ll match the price',
            })}
          </Heading>
          {intl.formatMessage({
            id: 'y2AK_Kaas4KP53KvMskT',
            defaultMessage: 'Once verified we’ll match the price.',
          })}
        </BodyText>
      </VerticalSpacer>}
      <HelpPane />
    </Modal>)
  }, [showModal, intl])

  const disableBestPriceGuarantee = useMemo(() => !Object.values(offer.bundledOffers).some((o) => o.type === 'hotel' && !o.disableBestPriceGuarantee), [offer.bundledOffers])

  return (<VerticalSpacer gap={8} className={className}>
    {config.bestPriceGuarantee && !disableBestPriceGuarantee && <USPItem
      data-type="bestPrice"
      onClick={onItemClick}
    >
      <BodyText
        variant="medium"
        colour="highlight-primary-dark"
        weight="bold"
        as="span"
      >
        {intl.formatMessage({
          id: 'lCgksbjPMeYCApzSTX7t',
          defaultMessage: 'Best price guarantee',
        })}
      </BodyText>
      <LineInfoCircleIcon colour="normal-contrast" size="XS" />
    </USPItem>}
  </VerticalSpacer>)
}

export default React.memo(BundleAndSaveUSPs)
